import { useRef, useState } from 'react'
import { RECORDING_HEAL_PROGRESS_EPSILON_S } from '@/config/constants'
import { useStableCallback } from '@/hooks/useStableCallback'
import { SupersededRecordingUrlRequest } from './useCallDetail'

/**
 * Shared recording-URL lifecycle for the audio players.
 *
 * VAPI serves recordings as Cloudflare R2 presigned URLs that expire, and
 * nothing in the URL tells us when. So we don't predict expiry — we react to
 * the browser telling us playback failed, resolve a genuinely fresh URL, and
 * resume. That makes recovery correct regardless of *why* the URL stopped
 * working: expiry, key rotation, revocation, or the backend having silently
 * served an unsigned URL when its own signing call failed.
 *
 * Owns the URL, the in-flight flag, re-entrancy, and the error/retry surface —
 * the parts both players need identically. Restoring playback position is
 * deliberately NOT here: `<audio>` just sets `currentTime`, while WaveSurfer has
 * to await a recreated instance and then `seekTo`. One interface over both would
 * be an adapter with two single-use implementations.
 */

export const RECORDING_LOAD_FAILED_MESSAGE = 'Could not load this recording.'

interface UseRecordingPlaybackParams {
  /** Already-signed URL from `useCallDetail`, when it has one. */
  resolvedRecordingUrl?: string | null
  /** Resolves a signed URL. `force` bypasses the cache, for recovery. */
  resolveRecordingUrl?: (options?: { force?: boolean }) => Promise<string>
  /** Resolve-in-flight flag owned by `useCallDetail`. */
  isRecordingUrlLoading?: boolean
  /** Recovery is exhausted — lets the parent drop its cached URL. */
  onResolvedRecordingUrlError?: () => void
  /**
   * A resolve landed on `url`. `unchanged` means it is the string already on
   * screen, so React writes no attribute, no media load runs, and the element
   * would sit on the dead resource with the error surface cleared — the player
   * has to force the reload itself.
   *
   * This lives here rather than in each player's error handler because the
   * *player* never sees the Retry path: `retry()` calls straight into the hook.
   * Notifying from the one place every resolve funnels through is what makes
   * automatic healing and Retry behave the same by construction.
   */
  onUrlSettled?: (url: string, info: { unchanged: boolean }) => void
}

interface UseRecordingPlaybackResult {
  /** URL to hand the media element, or null when we don't have one yet. */
  url: string | null
  /** A resolve is in flight, here or in `useCallDetail`. */
  isResolving: boolean
  /** Recovery failed. The player shows this with a Retry. */
  error: string | null
  /** Make sure a URL exists before starting playback. */
  ensure: () => Promise<string | null>
  /**
   * Recover after playback failed at `fromPosition` seconds. Null when
   * recovery is already spent for this stretch of playback.
   */
  heal: (fromPosition: number) => Promise<string | null>
  /** Named as a getter, not a flag: `if (isHealing)` would be always-true. */
  getIsHealing: () => boolean
  /** Playback reached `currentTime`; re-arms healing once it clears the point it died at. */
  notePlaybackProgress: (currentTime: number) => void
  /** User asked to try again after an error. */
  retry: () => Promise<string | null>
}

export function useRecordingPlayback({
  resolvedRecordingUrl,
  resolveRecordingUrl,
  isRecordingUrlLoading = false,
  onResolvedRecordingUrlError,
  onUrlSettled,
}: UseRecordingPlaybackParams): UseRecordingPlaybackResult {
  // Derived, not synced: a URL we resolved ourselves is newer than the prop, so
  // it wins; otherwise the prop shows through. Avoids a prop-sync effect, and
  // the players already remount per call, which resets this.
  const [healedUrl, setHealedUrl] = useState<string | null>(null)
  const [isResolvingLocally, setIsResolvingLocally] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const hasHealedRef = useRef(false)
  const healedAtRef = useRef(0)
  const isHealingRef = useRef(false)

  const url = healedUrl ?? resolvedRecordingUrl ?? null
  const urlRef = useRef(url)
  urlRef.current = url

  // Every consumer passes these as inline arrows, so without stabilising here
  // each parent render would churn every callback below and re-run the players'
  // media-listener and wavesurfer effects.
  const resolveUrl = useStableCallback(async (options?: { force?: boolean }) =>
    resolveRecordingUrl?.(options)
  )
  const notifyError = useStableCallback(() => onResolvedRecordingUrlError?.())
  const notifyUrlSettled = useStableCallback(
    (url: string, info: { unchanged: boolean }) => onUrlSettled?.(url, info)
  )

  const resolve = useStableCallback(async (options?: { force?: boolean }) => {
    if (!resolveRecordingUrl) return null
    setIsResolvingLocally(true)
    setError(null)
    try {
      const fresh = await resolveUrl(options)
      // Compared before the state update, so this is the URL still on screen.
      // Only ever true on a recovery path: `ensure` resolves solely when there
      // is no URL yet, so an unchanged result means a heal or Retry came back
      // with the same string — the case where nothing reloads on its own.
      const unchanged = fresh != null && fresh === urlRef.current
      setHealedUrl(fresh ?? null)
      if (fresh != null) notifyUrlSettled(fresh, { unchanged })
      return fresh ?? null
    } catch (error) {
      // A superseded request is a benign concurrency outcome: the newer resolve
      // that displaced it is already in flight and will land, so there is
      // nothing for the user to retry. Surfacing it would show a failure for a
      // request we cancelled on purpose.
      if (error instanceof SupersededRecordingUrlRequest) return null
      // Deliberately no `notifyError()` here. A failed *request* hasn't proven
      // the cached URL bad, and invalidating would blank the parent's URL and
      // re-trigger its prefetch effect — a hidden extra round trip per failure.
      setError(RECORDING_LOAD_FAILED_MESSAGE)
      return null
    } finally {
      setIsResolvingLocally(false)
    }
  })

  const ensure = useStableCallback(async () => urlRef.current ?? resolve())

  const heal = useStableCallback(async (fromPosition: number) => {
    if (isHealingRef.current || !resolveRecordingUrl) return null
    // Spent until playback actually gets past where it died. A recording that
    // fails at the same offset every time makes no progress, so it can never
    // re-arm — which is what stops it looping on the resolver forever. A real
    // later expiry always has progress behind it, so it heals freely.
    if (hasHealedRef.current) {
      setError(RECORDING_LOAD_FAILED_MESSAGE)
      // Recovery is genuinely exhausted, so dropping the parent's cached URL is
      // the point rather than a side effect.
      notifyError()
      return null
    }
    hasHealedRef.current = true
    healedAtRef.current = fromPosition
    isHealingRef.current = true
    try {
      return await resolve({ force: true })
    } finally {
      isHealingRef.current = false
    }
  })

  const getIsHealing = useStableCallback(() => isHealingRef.current)

  const notePlaybackProgress = useStableCallback((currentTime: number) => {
    if (!hasHealedRef.current) return
    if (currentTime > healedAtRef.current + RECORDING_HEAL_PROGRESS_EPSILON_S) {
      hasHealedRef.current = false
    }
  })

  // The user overriding the spent-until-progress rule.
  const retry = useStableCallback(() => {
    hasHealedRef.current = false
    return heal(healedAtRef.current)
  })

  return {
    url,
    isResolving: isResolvingLocally || isRecordingUrlLoading,
    error,
    ensure,
    heal,
    getIsHealing,
    notePlaybackProgress,
    retry,
  }
}
